import { useFormikContext } from "formik";
import React, { useEffect } from "react";

import IconButton from "../../../components/IconButton";
import ConditionalFieldsWrapper from "../../../components/ConditionalFieldsWrapper";
import Question from "../../../components/Question";
import QuestionDatepicker from "../../../components/QuestionDatepicker";
import RadioGroup from "../../../components/RadioGroup";
import QuestionAddress from "../../../components/QuestionAddress";
import { FlatApplciationType } from "../../../types";
import QuestionsFrame from "../../../components/QuestionsFrame";
import LongFootnote from "../../../components/LongFootnote";

type Props = {
  childIndex: number;
  childId: string;
  addChild: () => void;
  removeChild: (childId: string) => void;
  isLastChild?: boolean;
};

const Child: React.FC<Props> = ({
  childIndex,
  childId,
  addChild,
  removeChild,
  isLastChild = false,
}) => {
  const { errors, values, setFieldValue } = useFormikContext<FlatApplciationType>();
  // Find the global index of this child in the entire children array
  const globalIndex = Array.isArray(values.children) 
    ? values.children.findIndex(child => child.prp_id === childId)
    : -1;
  const isAddChild = childId.includes("add");
  
  // Set the child_action value whenever the component renders
  useEffect(() => {
    if (globalIndex !== -1) {
      setFieldValue(`children[${globalIndex}].child_action`, isAddChild ? 'add' : 'remove');
    }
  }, [globalIndex, isAddChild, setFieldValue]);

  return (
    <QuestionsFrame>
      <h4
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {`Child ${childIndex + 1}’s details`}
        {childIndex !== 0 && (
          <IconButton
            label="Remove"
            icon="plus"
            rotateIcon
            alert
            onClick={(e) => {
              e.preventDefault();
              removeChild(childId);
            }}
          />
        )}
      </h4>

      {/* <ConditionalFieldsWrapper conditions={{ seeking: "appl" }}>
            <RadioGroup name={`children[${globalIndex}].child_action`} displayRows />
          </ConditionalFieldsWrapper> */}
      <Question
        name={`children[${globalIndex}].prp_giv_nam`}
        label="Child’s first name*"
      />
      <Question
        name={`children[${globalIndex}].prp_giv_nam2`}
        label="Child’s middle name/s"
      />
      <Question
        name={`children[${globalIndex}].prp_fam_nam`}
        label="Child’s last name*"
      />
      <QuestionDatepicker
        name={`children[${globalIndex}].prp_dob`}
        label="Child’s date of birth*"
      />
      {isAddChild && (
        <>
          <RadioGroup
            name={`children[${globalIndex}].prp_live`}
            optionChildren={{
              no: (
                <QuestionAddress
                  namePrefix={`children[${globalIndex}].prp_addr`}
                  label="What address is the child currently living at?"
                />
              ),
            }}
          />
          <Question
            type="select"
            name={`children[${globalIndex}].prp_rel_to_appl`}
          />
          <ConditionalFieldsWrapper
            indent
            conditions={{ [`children[${globalIndex}].prp_rel_to_appl`]: "O" }}
          >
            <Question
              name={`children[${globalIndex}].prp_rel_to_appl_other`}
              label="Please specify"
            />
          </ConditionalFieldsWrapper>
          <Question
            type="select"
            name={`children[${globalIndex}].prp_rel_to_resp`}
          />
          <ConditionalFieldsWrapper
            indent
            conditions={{ [`children[${globalIndex}].prp_rel_to_resp`]: "O" }}
          >
            <Question
              name={`children[${globalIndex}].prp_rel_to_resp_other`}
              label="Please specify"
            />
          </ConditionalFieldsWrapper>
          <RadioGroup
            name={`children[${globalIndex}].prp_sex1`}
            label="Child’s gender"
          />
          <ConditionalFieldsWrapper
            conditions={{ [`children[${globalIndex}].prp_sex1`]: "U" }}
          >
            <Question name={`children[${globalIndex}].prp_sex_other`} />
          </ConditionalFieldsWrapper>
          <RadioGroup name={`children[${globalIndex}].prp_abor_code`} />
          <RadioGroup
            name={`children[${globalIndex}].prp_disabl`}
            optionChildren={{
              yes: (
                <LongFootnote
                  name={`children[${globalIndex}].prp_disabl_req_other`}
                  charLimit={4700}
                />
              )
            }}
          />
          <h5>Court Applications for Children</h5>
          <RadioGroup
            name={`children[${globalIndex}].prp_ord_exist`}
          />
        </>
      )}
      {childIndex < 5 && isLastChild && (
        <div>
          <IconButton
            disabled={!!(errors.children && errors.children[globalIndex])}
            onClick={(e) => {
              e.preventDefault();
              addChild();
            }}
            icon="plus"
            label="Add another child's details"
          />
        </div>
      )}
    </QuestionsFrame>
  );
};

export default Child;
